import { Tactic } from "@/types/tactic";
import { Head } from "@/components/base/Head";
import { Movie } from "@/components/tactic/Movie";
import { Manga } from "@/components/tactic/Manga";
import React, { useEffect, useState } from "react";
import { LpLayout } from "@/layouts/lp";
import { fetchTactic, newTacticRepository } from "@/repositories/tactic";
import { client } from "@/api/client";
import { useRouter } from "next/router";
import { Breadcrumb } from "@/components/base/Breadcrumb";
import { SectionTitle } from "@/components/base/ui/SectionTitle";
import { microCMSClient } from "@/utils/client";
import { Article } from "@/components/base/ui/Article";
import { Magazine } from "@/types/magazine";
import Image from "next/image";
import { useCurrentUser } from "@/hoc/useCurrentUser";
import { Quiz } from "@/components/tactic/Quiz";
import { Link as Scroll } from "react-scroll";
import useSWR from "swr";
import { Loading } from "@/components/base/Loading";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { LinkButton } from "@/components/base/ui/LinkButton";
import { RelatedTactics } from "@/components/tactic/RelatedTactics";
import { TacticCard } from "@/components/base/ui/TacticCard";
import { SimilarTactics } from "@/components/tactic/SimilarTactics";
import { ReadingTacticAction } from "@/components/tactic/ReadingTacticAction";
import { ReadingTactic } from "@/types/readingTactic";
import { ScrollToTop } from "@/components/base/ui/ScrollToTop";
import { FormSubmitButton } from "@/components/base/ui/FormSubmitButton";
import { useForm } from "react-hook-form";
import { gpt_order, gpt_rules, gpt_text } from "@/data/tactic_gpt_rule";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

const TacticPage = ({
  tacticData,
  magazine,
}: {
  tacticData: Tactic;
  magazine: Magazine | "";
}) => {
  const { isAuthChecking, currentUser } = useCurrentUser();
  const router = useRouter();
  const [isMovieEmpty, setIsMovieEmpty] = useState(false);
  const [tactic, setTactic] = useState(tacticData);
  const [readingTactics, setReadingTactics] = useState<ReadingTactic[]>([]);
  const [relatedTactics, setRelatedTactics] = useState<Tactic[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStart, setIsStart] = useState(false);
  const [aiText, setAiText] = useState("");
  const breadcrumbs = [
    {
      title: "講座一覧",
      slug: "/tactics",
    },
    {
      title: `${tactic?.tactic_primary?.name_ja}`,
      slug: `/tactic_primaries/${tactic?.tactic_primary?.id}`,
    },
    {
      title:
        tactic?.sort_order > 0
          ? `${tactic?.sort_order}. ${tactic?.title}`
          : tactic?.title,
    },
  ];

  const { data } = useSWR(`/api/v1/tactics/${router.query.id}`, (url) =>
    fetchTactic(url)
  );

  useEffect(() => {
    if (data) {
      setTactic(data);
    } else {
      setTactic(tacticData);
    }
  }, [data, router.pathname, tacticData]);

  useEffect(() => {
    if (isAuthChecking) return;
    if (!currentUser) router.push("/signin");
  }, [isAuthChecking, currentUser]);

  const generateJsonLd = (tactic: Tactic) => {
    const siteUrl = "https://app.s-d-lab.jp";
    const path = `/tactics/${tactic?.id}`;
    const url = `${siteUrl}${path}`;
    const defaultOgp = `${siteUrl}/ogp.jpg`;

    const imageUrl = defaultOgp;
    const jsonLd = {
      "@context": "http://schema.org",
      "@type": "Article",
      name: tactic?.title,
      headline: tactic?.title,
      datePublished: tactic?.created_at,
      dateModified: tactic?.created_at,
      url: url,
      mainEntityOfPage: url,
      image: [imageUrl],
      description: tactic?.body_html,
      author: {
        "@type": "Person",
        name: "戦略ラボ",
      },
      publisher: {
        "@type": "Organization",
        name: "戦略ラボ",
        logo: {
          "@type": "ImageObject",
          url: defaultOgp,
        },
      },
    };
    return JSON.stringify(jsonLd);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (formData: any) => {
    setIsLoading(true);
    setIsStart(true);
    setAiText("");
    const gpt_post_data = gpt_order + "# 講座" + data?.body_html + gpt_rules;
    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messages: [
            {
              role: "system",
              content: gpt_post_data,
            },
            {
              role: "user",
              content: formData.text,
            },
          ],
        }),
      });
      if (!response.body)
        throw new Error("No ReadableStream from the response.");
      const reader = response.body.getReader();

      async function readStream(): Promise<void> {
        const { done, value } = await reader.read();
        setIsLoading(false);
        if (done) return;
        const chunkValue = new TextDecoder().decode(value);
        setAiText((prev) => prev + chunkValue);
        await readStream();
      }

      await readStream();
    } catch (e) {}
  };

  const ScrollTrigger = ({ id, title }: { id: string; title: string }) => {
    return (
      <Scroll
        to={id}
        smooth={"easeInOutQuint"}
        duration={500}
        offset={-66}
        className={
          "text-blue-700 font-bold rounded-full inline-block px-5 py-2 border border-blue-700 hover:bg-blue-700 hover:text-white duration-300 cursor-pointer"
        }
      >
        {title}
      </Scroll>
    );
  };

  const actionBanner = (textColor: string) => {
    return (
      <div className={`${textColor}`}>
        <p className={`md:text-3xl text-xl mb-3 font-bold`}>
          <span className={"leading-[1.5]"}>
            無料会員登録で
            <br className={"hidden md:block"} />
            閲覧可能に！
          </span>
        </p>
        <ul className={"text-sm md:text-xl font-bold"}>
          <li className={"flex mb-1"}>
            <CheckBadgeIcon className={"md:w-7 w-5 mr-2 text-green-400"} />
            プレミアムコンテンツが1日1つ閲覧可能
          </li>
          <li className={"flex mb-1"}>
            <CheckBadgeIcon className={"md:w-7 w-5 mr-2 text-green-400"} />
            集合研修のお知らせあり
          </li>
        </ul>
        <div className={"mt-4"}>
          {currentUser && !currentUser?.is_subscribed ? (
            <LinkButton
              title={"登録する"}
              slug={"/billing"}
              classes={"bg-blue-700 text-white md:text-2xl"}
            />
          ) : (
            <LinkButton
              title={"登録する"}
              slug={"/plans"}
              classes={"bg-blue-700 text-white md:text-2xl"}
            />
          )}
        </div>
      </div>
    );
  };

  const nextTactic: Tactic | undefined = relatedTactics[tactic.sort_order];

  const isReading = () => {
    if (tactic.is_free) {
      return true;
    } else if (currentUser?.is_subscribed) {
      return true;
    } else if (readingTactics.map((_) => _.tactic_id).includes(tactic.id)) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      <Head
        title={tactic?.title}
        description={tactic?.summary}
        jsonLd={generateJsonLd(tactic)}
        canonicalPath={`/tactics/${tactic?.id}`}
      />
      <main className={"relative pb-20 tactic"}>
        <div className={"bg-gray-900 text-center"}>
          {!tactic?.is_free && !currentUser ? (
            <div className={"relative text-center"}>
              <div>
                {tactic?.image && (
                  <Image
                    src={tactic?.image}
                    width={1200}
                    height={630}
                    alt={""}
                  />
                )}
              </div>
              <div
                className={
                  "flex justify-center items-center absolute top-0 left-0 right-0 bottom-0 h-full bg-black bg-opacity-80"
                }
              >
                {actionBanner("text-white")}
              </div>
            </div>
          ) : !isReading() ? (
            <div className={"relative text-center min-h-[400px]"}>
              <div>
                {tactic?.image && (
                  <Image
                    src={tactic?.image}
                    width={1200}
                    height={630}
                    layout={"fill"}
                    objectFit={"cover"}
                    className={"w-full h-full"}
                    alt={""}
                  />
                )}
              </div>
              <div
                className={
                  "flex justify-center items-center absolute top-0 left-0 right-0 bottom-0 h-full bg-black bg-opacity-80"
                }
              >
                <ReadingTacticAction
                  tactic={tactic}
                  readingTactics={(data) => {
                    setReadingTactics(data);
                  }}
                />
              </div>
            </div>
          ) : tactic?.tactic_sliders?.length > 0 ? (
            <Manga tactic={tactic} />
          ) : isMovieEmpty ? (
            <div>
              <Image src={tactic?.image} width={1200} height={630} alt={""} />
            </div>
          ) : (
            <div className={"text-center"}>
              <Movie
                tactic_id={String(router.query.id)}
                isEmpty={() => setIsMovieEmpty}
              />
            </div>
          )}
        </div>
        <div className={"mb-10 max-w-[1440px] m-auto px-4 py-2 relative"}>
          <Breadcrumb breadcrumbs={breadcrumbs} />
        </div>
        <div className={"max-w-[1280px] m-auto md:px-14 md:flex relative"}>
          <div className={"flex-1 mb-20 md:mb-0 relative"}>
            <div className={"md:flex mb-12 md:px-0 px-5"}>
              <div className={"mr-3 mb-2 md:mb-0"}>
                <ScrollTrigger id={"explanation"} title={"解説を見る"} />
              </div>
              {currentUser?.is_subscribed && (
                <ScrollTrigger id={"quiz"} title={"クイズ回答で受講完了"} />
              )}
            </div>
            <div className={"mb-10 px-5 md:px-0"} id={"explanation"}>
              <SectionTitle title={tactic?.title} />
            </div>
            {isReading() &&
              tactic?.tactic_sliders.length > 0 &&
              !isMovieEmpty && (
                <>
                  <div className={"mb-8"}>
                    <Movie
                      tactic_id={String(router.query.id)}
                      isEmpty={() => setIsMovieEmpty}
                    />
                    <div className={"md:px-0 px-3"}>
                      <a
                        href="https://tayori.com/faq/95dd3fab44b53f9e7089a48211fbca96638ff9ef/detail/cf7c44989932e1ef6847860f89afb2557497a1f6/"
                        className={
                          "underline text-blue-800 mt-2 inline-block text-sm"
                        }
                        target={"_blank"}
                        rel="noreferrer"
                      >
                        動画を倍速で再生する方法
                      </a>
                    </div>
                  </div>
                </>
              )}
            <div
              className={`relative px-3 md:px-0 ${
                !isReading() && "min-h-[650px] max-h-[1000px] overflow-hidden"
              }`}
            >
              {magazine ? (
                <div className={"mb-20"}>
                  <Article description={magazine.description} />
                </div>
              ) : (
                <div
                  className={"text mb-10"}
                  dangerouslySetInnerHTML={{
                    __html: tactic?.body_html,
                  }}
                />
              )}
              {!isReading() && (
                <div
                  className={
                    "flex items-center justify-center absolute bottom-0 left-0 right-0 bg-white bg-opacity-90 h-[400px]"
                  }
                >
                  {actionBanner("text-blue-800")}
                </div>
              )}
            </div>
            <div className={"mb-20"}>
              <div className={"mb-5"}>
                <SectionTitle title={tactic.tactic_primary.name_ja} />
              </div>
              <RelatedTactics
                tactic={tactic}
                relatedTactics={(data) => setRelatedTactics(data)}
              />
            </div>

            {currentUser?.is_subscribed && (
              <div id={"quiz"} className={"md:px-0 px-5"}>
                <SectionTitle title={"クイズ"} />
                <Quiz
                  quizzes={tactic?.tactic_quizzes}
                  tactic_id={tactic?.id}
                  experience_point={tactic?.experience_point}
                  tactic_quizzes_count={tactic?.tactic_quizzes_count}
                  is_attended={tactic?.is_attended}
                />
              </div>
            )}

            <div className={"mt-20 mb-5"}>
              <SectionTitle title="受講理解度チェック" />
            </div>
            <div
              className={
                "px-6 py-5 border border-blue-700 rounded-lg bg-blue-50 flex items-start mb-2 shadow"
              }
            >
              <div>
                <p>
                  本講座を受講した感想を入力してください。入力内容に応じて即時にフィードバックを行います。文字数制限はありません。講座を受講してあなたが「重要だと考えたポイント」「なぜそのポイントが重要だと考えたか」「今後自身が取り組まなければいけないこと」を記載してください。
                </p>
              </div>
            </div>
            <form
              onSubmit={handleSubmit((data) => onSubmit(data))}
              className={"mb-10"}
            >
              <textarea
                rows={10}
                className={"bg-gray-50 rounded w-full"}
                {...register("text", { required: true })}
              >
                {gpt_text}
              </textarea>
              {errors.text && (
                <span className={"text-red-500 text-sm"}>必須項目です。</span>
              )}
              <div className={"flex justify-center mt-5"}>
                <FormSubmitButton>フィードバックを開始する</FormSubmitButton>
              </div>
            </form>
            {isLoading ? (
              <Loading />
            ) : (
              isStart && (
                <div
                  className={
                    "px-6 py-5 border border-blue-700 rounded-lg bg-blue-50 mb-20"
                  }
                >
                  <p
                    className={
                      "flex justify-center items-center font-bold text-lg mb-4 text-blue-700"
                    }
                  >
                    <CheckCircleIcon className={"w-5 mr-2"} />
                    結果
                  </p>
                  <p className={"font-bold leading-[1.75] whitespace-pre-wrap"}>
                    {aiText}
                  </p>
                </div>
              )
            )}

            {tactic?.is_attended && (
              <div className={"absolute top-0 right-0 z-10"}>
                <Image
                  src={"/images/attendance.png"}
                  alt={"受講済み"}
                  width={120}
                  height={120}
                />
              </div>
            )}
            {tactic?.status === "draft" && (
              <div className={"absolute top-0 right-0 z-10"}>
                <Image
                  src={"/images/draft.png"}
                  alt={"受講済み"}
                  width={120}
                  height={120}
                />
              </div>
            )}
          </div>
          <div className={"md:pl-6 md:w-[258px]"}>
            {nextTactic && (
              <div className={"mb-10 px-5 md:px-0"}>
                <div className={"mb-3"}>
                  <SectionTitle title={"次回"} isBorder={false} />
                </div>
                <TacticCard tactic={nextTactic} />
              </div>
            )}
            <div className={"px-5 md:px-0"}>
              <div className={"mb-3"}>
                <SectionTitle
                  title={"関連コンテンツ"}
                  isBorder={nextTactic != null}
                />
              </div>
              <SimilarTactics targetTactic={tactic} />
            </div>
          </div>
        </div>
        <ScrollToTop />
      </main>
    </>
  );
};

TacticPage.Layout = LpLayout;
export async function getStaticPaths() {
  const tacticsRepository = newTacticRepository(client);
  const response = await tacticsRepository.listTactics(`/api/v1/tactics`);
  const paths = response.map((content) => ({
    params: { id: content.id },
  }));
  return { paths, fallback: false };
}
export async function getStaticProps(context: { params: { id: string } }) {
  const tacticsRepository = newTacticRepository(client);
  try {
    let magazine = "";
    const response = await tacticsRepository.getTactic(
      context.params.id as string
    );

    if (response.microcms_id) {
      magazine = await microCMSClient.get({
        endpoint: "magazine",
        contentId: response.microcms_id,
      });
    }

    return {
      props: {
        tacticData: response,
        magazine,
      },
    };
  } catch (e) {
    return {
      redirect: {
        destination: `/404`,
        permanent: false,
      },
    };
  }
}
export default TacticPage;
